import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Monitor, Pencil, Plus, RotateCcw } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { CashTerminalSummary } from '@aeris/contracts'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  Input,
  RowOpener,
  Select,
  SortableHeaderCell,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import {
  ApiError,
  createCashTerminal,
  fetchCashTerminals,
  updateCashTerminal,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'

/**
 * The till register (F025).
 *
 * Until this screen existed the caixa was typed at the start of every shift,
 * and `cash_session.terminal_id` is a plain string — so a typo did not fail,
 * it opened a second session on one drawer and split the day's cash across two
 * shifts that looked like one.
 *
 * Two rules the screen has to make visible, because both surprise people:
 *
 *   * **The code cannot be edited.** It is written verbatim onto every session
 *     the till ever ran, so changing it would leave that history naming
 *     something that no longer exists. A till whose code is wrong is archived
 *     and replaced — which is also what happens to the drawer.
 *
 *   * **A code is never released.** Archiving takes a till out of the list; it
 *     does not free its name, for the same reason.
 */

export const TerminaisPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()

  const [status, setStatus] = useState<'active' | 'archived'>('active')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<CashTerminalSummary | null>(null)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const sort = useTableSort<'code' | 'name'>('code', 'asc')

  // Which drawers a shop has is a supervisory fact, not an operator's call.
  const canManage = allows('pos.supervise')

  const query = useQuery({
    queryKey: ['cash', 'terminals', { status, ...sortKey(sort) }],
    queryFn: ({ signal }) =>
      fetchCashTerminals(
        { status, sort: sort.sort, direction: sort.direction },
        signal
      ),
  })

  const terminals = query.data?.items ?? []

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['cash', 'terminals'] })
  }

  const failed = (cause: unknown, fallback: string): void => {
    setError(cause instanceof ApiError ? cause.message : fallback)
  }

  const save = useMutation({
    mutationFn: async () =>
      editing === null
        ? await createCashTerminal({ code, name })
        : await updateCashTerminal(editing.id, {
            name,
            version: editing.version,
          }),
    onSuccess: async () => {
      setFormOpen(false)
      setError(null)
      await invalidate()
    },
    onError: (cause: unknown) => {
      failed(cause, 'Não foi possível salvar o caixa.')
    },
  })

  const setStatusOf = useMutation({
    mutationFn: async (input: {
      terminal: CashTerminalSummary
      next: 'active' | 'archived'
    }) =>
      await updateCashTerminal(input.terminal.id, {
        status: input.next,
        version: input.terminal.version,
      }),
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (cause: unknown) => {
      failed(cause, 'Não foi possível alterar o caixa.')
    },
  })

  const openNew = (): void => {
    setEditing(null)
    setCode('')
    setName('')
    setError(null)
    setFormOpen(true)
  }

  const openEdit = (terminal: CashTerminalSummary): void => {
    setEditing(terminal)
    setCode(terminal.code)
    setName(terminal.name)
    setError(null)
    setFormOpen(true)
  }

  return (
    <>
      <TabHeader
        title="Terminais"
        description="Os caixas físicos desta filial. O operador escolhe um deles ao abrir o turno."
        actions={
          canManage ? (
            <Button
              variant="primary"
              size="sm"
              onClick={openNew}
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
            >
              Novo caixa
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as 'active' | 'archived')
          }}
          aria-label="Situação"
          className="w-44"
        >
          <option value="active">Ativos</option>
          <option value="archived">Arquivados</option>
        </Select>
      </div>

      {error === null ? null : (
        <p
          role="alert"
          className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
        >
          {error}
        </p>
      )}

      {query.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : query.isError ? (
        <EmptyState
          icon={<Monitor aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar os caixas"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : terminals.length === 0 ? (
        <EmptyState
          icon={<Monitor aria-hidden="true" className="h-8 w-8" />}
          title={
            status === 'archived'
              ? 'Nenhum caixa arquivado'
              : 'Nenhum caixa cadastrado'
          }
          description={
            status === 'archived'
              ? 'Caixas arquivados continuam legíveis nos turnos já fechados.'
              : canManage
                ? 'Cadastre um por balcão, com o nome que está escrito nele: CAIXA 01, PDV BALCÃO.'
                : 'Peça a um supervisor para cadastrar os caixas desta filial.'
          }
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeaderCell {...sort.column('code')}>
                  Código
                </SortableHeaderCell>
                <SortableHeaderCell {...sort.column('name')}>
                  Nome
                </SortableHeaderCell>
                <TableHeaderCell>Filial</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                {canManage ? (
                  <TableHeaderCell numeric>Ações</TableHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {terminals.map((terminal) => (
                <TableRow
                  key={terminal.id}
                  {...(canManage
                    ? {
                        onOpen: () => {
                          openEdit(terminal)
                        },
                      }
                    : {})}
                >
                  <TableCell className="font-(--font-mono)">
                    {canManage ? (
                      <RowOpener
                        onOpen={() => {
                          openEdit(terminal)
                        }}
                      >
                        {terminal.code}
                      </RowOpener>
                    ) : (
                      terminal.code
                    )}
                  </TableCell>
                  <TableCell>{terminal.name}</TableCell>
                  <TableCell>{terminal.branchName}</TableCell>
                  <TableCell>
                    <Badge
                      tone={
                        terminal.status === 'active' ? 'success' : 'neutral'
                      }
                    >
                      {terminal.status === 'active' ? 'Ativo' : 'Arquivado'}
                    </Badge>
                  </TableCell>
                  {canManage ? (
                    <TableCell numeric>
                      <div
                        className="flex justify-end gap-1"
                        /* Sem `RowActions` aqui, então o clique das ações tem
                           de ser parado à mão: "Arquivar" não pode abrir o
                           Renomear por baixo. */
                        onClick={(event) => {
                          event.stopPropagation()
                        }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            openEdit(terminal)
                          }}
                          leadingIcon={
                            <Pencil aria-hidden="true" className="h-4 w-4" />
                          }
                        >
                          Renomear
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={setStatusOf.isPending}
                          onClick={() => {
                            setStatusOf.mutate({
                              terminal,
                              next:
                                terminal.status === 'active'
                                  ? 'archived'
                                  : 'active',
                            })
                          }}
                          leadingIcon={
                            terminal.status === 'active' ? (
                              <Archive aria-hidden="true" className="h-4 w-4" />
                            ) : (
                              <RotateCcw
                                aria-hidden="true"
                                className="h-4 w-4"
                              />
                            )
                          }
                        >
                          {terminal.status === 'active'
                            ? 'Arquivar'
                            : 'Reativar'}
                        </Button>
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        {formOpen ? (
          <DialogContent
            title={editing === null ? 'Novo caixa' : 'Renomear caixa'}
            description={
              editing === null
                ? 'O código é o que está escrito no balcão. Ele não pode ser alterado depois.'
                : 'O código não muda: ele já está gravado em todos os turnos deste caixa.'
            }
          >
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault()
                save.mutate()
              }}
            >
              <Field
                label="Código"
                hint={
                  editing === null
                    ? 'Ex.: CAIXA 01. Vira maiúsculas automaticamente.'
                    : 'Não editável'
                }
              >
                {(field) => (
                  <Input
                    {...field}
                    value={code}
                    disabled={editing !== null}
                    maxLength={40}
                    autoFocus={editing === null}
                    onChange={(event) => {
                      setCode(event.target.value)
                    }}
                  />
                )}
              </Field>

              <Field label="Nome" hint="Como as pessoas se referem a ele">
                {(field) => (
                  <Input
                    {...field}
                    value={name}
                    maxLength={80}
                    autoFocus={editing !== null}
                    onChange={(event) => {
                      setName(event.target.value)
                    }}
                  />
                )}
              </Field>

              <DialogFooter>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setFormOpen(false)
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={save.isPending}
                  disabled={code.trim() === '' || name.trim() === ''}
                >
                  Salvar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  )
}
